import formatUTCDate from '@/shared/tools/formatUTCDate'
import type { ContactSubscriptionData } from './contactSubscriptionData'

type CoreParameters = {
  contactData: ContactSubscriptionData
  id: string | number
  country: string
  action: string
}

type BaseParameters = CoreParameters & {
  doorToDoorPartnerChecklistCheckedTitles: Record<string, boolean>
  doorToDoorPartnerLeadSource: string
}

// Shared by all 5 countries
export function buildCoreContactParameters({ contactData, id, country, action }: CoreParameters): URLSearchParams {
  const options = new URLSearchParams()

  const {
    personal: {
      zohoId,
      civility = '-1',
      lastName = '',
      firstName = '',
      email = '',
      phone1 = '',
      phone2 = '',
      birthdate = '',
    },
    preferences: { dataCollectionAuth, neverBounceResult, callbackTime, callbackDate, clientType },
    professional: { socialReason },
    additional: { comments = '' } = {},
  } = contactData

  if (action !== '') options.append('action_requested', action)

  options.append('id', String(id))
  options.append('zoho_id', zohoId ?? '')
  options.append('lang', country)

  options.append('disclaimer', dataCollectionAuth ? '1' : '0')
  options.append('neverbounce_result', String(neverBounceResult ?? ''))

  if (callbackTime && callbackDate && callbackTime !== '' && callbackDate !== '') {
    const date = new Date(`${callbackDate} ${callbackTime}`).toISOString().slice(0, 19)
    options.append('web_callback', formatUTCDate(date) || '')
  }

  options.append('civility', civility || '')
  options.append('lastname', lastName || '')
  options.append('firstname', firstName || '')
  options.append('email', email || '')
  if (birthdate) options.append('birthday', birthdate || '')
  options.append('phone1', phone1 || '')
  options.append('phone2', phone2 || '')

  options.append('is_professional', clientType === 'pro' ? '1' : '0')
  options.append('pro_social_reason', socialReason || '')

  if (comments) options.append('subscription_comments', comments)

  return options
}

// Extends core with fields shared by FR, BE, NL, DE (not IT)
export function buildBaseContactParameters(parameters: BaseParameters): URLSearchParams {
  const options = buildCoreContactParameters(parameters)

  const {
    contactData: {
      personal: { civility2 = '-1', lastName2 = '', firstName2 = '', secondName },
      preferences: { whatsappConsent, createReviewsInteraction, reviewLanguage },
      additional: { referral, referralFirstName = '', referralLastName = '' } = {},
    },
    doorToDoorPartnerChecklistCheckedTitles,
    doorToDoorPartnerLeadSource,
  } = parameters

  options.append('whatsapp_consent', whatsappConsent ? '1' : '0')
  if (createReviewsInteraction) {
    options.append('create_reviews_interaction', '1')
    if (reviewLanguage) options.append('review_language', reviewLanguage)
  }

  options.append('secondName', secondName === true ? '1' : '0')
  if (secondName) {
    options.append('civility2', civility2 || '')
    options.append('lastname2', lastName2 || '')
    options.append('firstname2', firstName2 || '')
  }

  options.append('referral', referral === true ? '1' : '0')
  if (referral) {
    options.append('referral_firstname', referralFirstName || '')
    options.append('referral_lastname', referralLastName || '')
  }

  const allChecked = Object.values(doorToDoorPartnerChecklistCheckedTitles).every(Boolean)
  if (allChecked && doorToDoorPartnerLeadSource) {
    options.append('log_door_to_door_partner_completed_checklist', doorToDoorPartnerLeadSource)
  }

  return options
}
